/* eslint-disable react-perf/jsx-no-new-function-as-prop */

import React from 'react';
import { connect } from 'react-redux';

import { AppContext } from '@prisjakt/ui-app-context';
import { noop } from '@prisjakt/ui-base';

import DataContainer from '../../../../components/data';
import ItemList from '../../components/item-list';
import EmptyList from '../../components/empty-list';
import Loader from '../../../../components/loader';
import SortButton from '../../components/sort-button';
import AlertEdit from '../../../product-alert/components/product-alert-modal-content';
import Modal from '../../../../components/modal';
import SidebarHeaderDesktop from '../../components/sidebar-desktop/sidebar-header-desktop';
import SidebarHeaderMobile from '../../components/sidebar-mobile/sidebar-header-mobile';

import modalActions from '../../../../components/modal/actions';
import actions from '../../actions/alert';
import listActions from '../../actions/lists';
import {
  alertSettingsSaveRequest,
  alertSettingsChange,
} from '../../../account/settings/actions/alert-settings';
import alertsQuery from '../../alerts-query.graphql';

import '../../../account/settings/sagas/alerts';

import AlertSettings from '../../../account/settings/components/alert-settings';

import { ListItemFragment, AlertsQuery } from '../../../../types';
import { Sort } from '../../components/sharedTypes';
import {
  View,
  UserProductAlert,
  UserProductAlerts,
  RemoveAlertRequest,
} from '../../types';
import { AppState } from '../../../../reducers';
import UnauthenticatedAlerts from '../../components/item-list/unauthenticated-alerts';

import { VIEWS } from '../../components/sidebar-mobile/constants';

import * as S from './styles';

const PRODUCT_ALERT_MODAL_ID = 'product-alert-modal-sidebar';

interface AlertsProps {
  removeAlertRequest: RemoveAlertRequest;
  restoreAlertRequest: () => void;
  sortAlerts: (sort: { name: string; value: Sort }) => void;
  alertSettingsSaveRequest: (settings: AlertSettings) => void;
  alertSettingsChange: (
    settings: Array<{ setting: string; value: boolean }>
  ) => void;
  onEditAlert?: () => void;
  hideModal: () => void;
  showModal: (id: string) => void;
  isAuthenticated: boolean;
  onLinkClick?: () => void;
  onOpenView?: (view: View) => void;
  isMobile?: boolean;
}

interface State {
  showSettings: boolean;
  alertSettings: AlertSettings;
  editAlert?: ListItemFragment;
}

interface StateProps {
  alertSettings: AlertSettings;
  alertsState: {
    direction?: 'desc' | 'asc';
    sort: Sort;
    products: UserProductAlerts;
  };
}

type Props = AlertsProps & StateProps;

type AlertSettings = {
  email: boolean;
  push: boolean;
};

type DataProps = {
  data: AlertsQuery;
  fetchMore: () => void;
  fetching: boolean;
};

class Alerts extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      showSettings: false,
      alertSettings: props.alertSettings,
      editAlert: undefined,
    };
  }

  static graphQL = alertsQuery;

  onEditAlert = (alert: ListItemFragment) => {
    this.setState({ editAlert: alert }, () => {
      this.props.showModal(PRODUCT_ALERT_MODAL_ID);
    });
  };

  closeEditAlert = () => {
    this.setState({ editAlert: undefined });
  };

  saveAlertSettings() {
    const { alertSettings } = this.props;

    this.props.alertSettingsSaveRequest(alertSettings);
    this.setState({
      alertSettings,
      showSettings: false,
    });
  }

  cancelAlertSettings() {
    this.props.alertSettingsChange(
      Object.keys(this.state.alertSettings).map(key => ({
        setting: key,
        value: this.state.alertSettings[key],
      }))
    );
    this.setState({
      showSettings: false,
    });
  }

  openSettings = () => {
    this.setState({ showSettings: true });
  };

  closeSettings = () => {
    this.setState({ showSettings: false });
  };

  openRootView = () => {
    return this.props.onOpenView && this.props.onOpenView(VIEWS.ROOT);
  };

  render() {
    const {
      alertsState,
      removeAlertRequest,
      sortAlerts,
      hideModal,
      isAuthenticated,
      onLinkClick,
      isMobile,
      onOpenView,
    } = this.props;
    const { t } = this.context;

    if (!isAuthenticated) {
      return (
        <UnauthenticatedAlerts
          isMobile={isMobile}
          onLoginClick={onLinkClick}
          onHeaderClick={this.openRootView}
        />
      );
    }

    if (this.state.showSettings) {
      return (
        <>
          {isMobile ? (
            <SidebarHeaderMobile
              title={t('account', 'menu.settings')}
              onClickHandler={this.closeSettings}
            />
          ) : (
            <SidebarHeaderDesktop
              title={t('account', 'menu.settings')}
              goBack={this.closeSettings}
            />
          )}
          <S.AlertSettings>
            <AlertSettings shouldSave />
          </S.AlertSettings>
        </>
      );
    }

    if (this.state.editAlert && isMobile) {
      return (
        <>
          <SidebarHeaderMobile
            title={t('alerts', 'alerts.title')}
            options={[{ handler: this.openSettings }]}
            onClickHandler={this.closeEditAlert}
          />

          <AlertEdit
            product={
              'product' in this.state.editAlert && this.state.editAlert.product
            }
            close={this.closeEditAlert}
            withoutGraph
          />
        </>
      );
    }

    const refreshKey = alertsState.products
      .map((alert: UserProductAlert) => `${alert.alertId}:${alert.productId}`)
      .join(':');

    return (
      <>
        <DataContainer
          key={`alerts-${alertsState.sort}`}
          query={Alerts.graphQL}
          renderLoader={() => <Loader fadeIn />}
          refreshKey={refreshKey}
          variables={{
            limit: 30,
            sort: alertsState.sort,
          }}
          // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
          render={(renderProps: DataProps) => {
            const { data, fetchMore, fetching } = renderProps;

            if (!data || !data.me || !data.me.alerts) {
              return null;
            }

            const { alerts } = data.me;

            return (
              <>
                {!isMobile && this.state.editAlert && (
                  <Modal
                    id={PRODUCT_ALERT_MODAL_ID}
                    onRequestClose={hideModal}
                    width={500}
                  >
                    <AlertEdit
                      product={
                        'product' in this.state.editAlert &&
                        this.state.editAlert.product
                      }
                      close={hideModal}
                    />
                  </Modal>
                )}

                {isMobile ? (
                  <SidebarHeaderMobile
                    title={t('alerts', 'alerts.title')}
                    options={[{ handler: this.openSettings }]}
                    onClickHandler={
                      onOpenView && (() => onOpenView(VIEWS.ROOT))
                    }
                  />
                ) : (
                  <SidebarHeaderDesktop
                    title={t('alerts', 'alerts.title')}
                    options={[{ handler: this.openSettings }]}
                  />
                )}

                {alerts.pageInfo.sort && alerts.nodes.length > 0 && (
                  <SortButton
                    sort={alerts.pageInfo.sort}
                    onSelect={(sort: { name: string; value: Sort }) => {
                      sortAlerts(sort);
                    }}
                  />
                )}

                {alerts && (
                  <ItemList
                    pageInfo={alerts.pageInfo}
                    nodes={alerts.nodes}
                    fetching={fetching}
                    onClick={onLinkClick || noop}
                    onFetchMore={() => {
                      fetchMore();
                    }}
                    renderEmpty={
                      <EmptyList
                        text={t('alerts', 'list.empty-helper-text')}
                        icon={<S.AlertIcon size={64} />}
                      />
                    }
                    onDelete={(id: number, productId?: number) =>
                      removeAlertRequest(id, productId)
                    }
                    onEdit={(alert: ListItemFragment) => {
                      this.onEditAlert(alert);
                    }}
                  />
                )}
              </>
            );
          }}
        />
      </>
    );
  }
}

Alerts.contextType = AppContext;

const mapStateToProps = (state: AppState) => ({
  alertSettings: state.areas.account.me && state.areas.account.me.alertSettings,
  alertsState: state.areas.sidebar.alerts,
  isAuthenticated: state.areas.account.isAuthenticated,
});

const dispatchProps = {
  ...listActions,
  ...actions,
  ...modalActions,
  alertSettingsSaveRequest,
  alertSettingsChange,
};

export default connect(mapStateToProps, dispatchProps)(Alerts);
